import {
	object, ref, string,
} from 'yup';
import {
	CreateUserMutationVariables,
	MutationUpdateUserArgs, PendingInvite,
	RedditGroupsForUsersQuery, UserAccountLevel,
	UserGroupRole, UserMetaDataQueryMe,
} from 'wb-reddit/graphql/generated';
import {
	UserGroupAccess,
	MutationCreateUserArgs,
	MutationUpdateUserArgs as AuthGraphMutationUpdateUserArgs,
} from 'wb-spotify/graphql/authgraph/generated';
import { ArrayElement, isReddit } from '@aleph/shared-components';
import { UserOptionType } from 'wb-reddit/app/routes/teams/utils';
import useUserMetaData from 'wb-reddit/app/common/hooks/useUserMetaData';

export type MutatingRedditUser = Partial<MutationUpdateUserArgs & CreateUserMutationVariables & {
	userProfilePicture?: any,
	userGroups?: UserGroupOption[],
	userGroupAccess?: UserGroupAccess[],
	userEmailConfirmation?: string,
	pendingInvites?: PendingInvite[]
}>;

export type MutatingAuthGraphUser = MutationCreateUserArgs & AuthGraphMutationUpdateUserArgs & {
	userEmailConfirmation?: string,
	userPasswordConfirmation?: string,
	userGroups?: UserGroupOption[],
};

export type UserGroupOption = ArrayElement<RedditGroupsForUsersQuery['userGroups']['items']>;

const validateUserEmailForUserRoles = (email, platformUser, userAccountLevel: UserAccountLevel) => {
	const emailDomain = email?.split('@')?.[1];
	const platformUserEmailDomain = platformUser?.userEmail?.split('@')?.[1];

	if (emailDomain === platformUserEmailDomain) {
		return true;
	}

	if (
		(
			platformUser?.userAccountLevel === UserAccountLevel.Agency
			&& userAccountLevel === UserAccountLevel.Advertiser
		)
		|| (
			platformUser?.userAccountLevel === UserAccountLevel.Advertiser
			&& userAccountLevel === UserAccountLevel.Agency
		)
	) {
		return true;
	}

	return platformUser?.userAccountLevel === UserAccountLevel.Admin;
};

const validateUserEmailNoMatchForUserRoles = (email, platformUser, userAccountLevel: UserAccountLevel) => {
	const emailDomain = email?.split('@')?.[1];
	const platformUserEmailDomain = platformUser?.userEmail?.split('@')?.[1];

	if (emailDomain !== platformUserEmailDomain) {
		return true;
	}

	if (
		(
			platformUser?.userAccountLevel === UserAccountLevel.Agency
			&& userAccountLevel !== UserAccountLevel.Advertiser
		)
		|| (
			platformUser?.userAccountLevel === UserAccountLevel.Advertiser
			&& userAccountLevel !== UserAccountLevel.Agency
		)
	) {
		return true;
	}

	return platformUser?.userAccountLevel === UserAccountLevel.Admin;
};

export const getCreateUserValidationSchema = (platformUser?: UserMetaDataQueryMe) => {
	if (isReddit()) {
		return object({
			userName:    string().required('Input is required'),
			userSurname: string().required('Input is required'),
			userEmail:   string()
				.test({
					name:    'emailMatches',
					message: `Domain must match ${platformUser?.userEmail?.split('@')?.[1]}`,
					test:    (value, context) => validateUserEmailForUserRoles(value, platformUser, context.parent.userAccountLevel),
				})
				.test({
					name:    'emailDoesntMatch',
					message: `Domain must NOT match ${platformUser?.userEmail?.split('@')?.[1]}`,
					test:    (value, context) => validateUserEmailNoMatchForUserRoles(value, platformUser, context.parent.userAccountLevel),
				})
				.matches(/^[\w-.+]+@([\w-]+\.)+[\w-]{2,4}$/g, 'Use a valid E-mail address')
				.required('Input is required')
				.oneOf(
					[ref('userEmailConfirmation')],
					'E-Mails must match',
				),
			userEmailConfirmation: string().oneOf(
				[ref('userEmail')],
				'E-Mails must match',
			),
		});
	}

	return object({
		userName:    string().required('Input is required'),
		userSurname: string().required('Input is required'),
		userEmail:   string()
			.matches(/^[\w-.+]+@([\w-]+\.)+[\w-]{2,4}$/g, 'Use a valid E-mail address')
			.required('Input is required')
			.oneOf(
				[ref('userEmailConfirmation')],
				'E-Mails must match',
			),
		userEmailConfirmation: string().oneOf(
			[ref('userEmail')],
			'E-Mails must match',
		),
		userPassword:
			string()
				.min(8, 'Password must be at least 8 characters')
				.required('Password is required')
				.oneOf(
					[ref('userPasswordConfirmation')],
					'Passwords must match',
				),
		userPasswordConfirmation: string().oneOf(
			[ref('userPassword')],
			'Passwords must match',
		),
	});
};

export const getUpdateUserValidationSchema = () => {
	if (isReddit()) {
		return object({
			userName:    string().required('Input is required'),
			userSurname: string().required('Input is required'),
		});
	}

	return object({
		userName:    string().required('Input is required'),
		userSurname: string().required('Input is required'),
		userEmail:   string()
			.matches(/^[\w-.+]+@([\w-]+\.)+[\w-]{2,4}$/g, 'Use a valid E-mail address')
			.oneOf(
				[ref('userEmailConfirmation')],
				'E-Mails must match',
			),
		userEmailConfirmation: string().oneOf(
			[ref('userEmail')],
			'E-Mails must match',
		),
		userPassword:
			string()
				.min(8, 'Password must be at least 8 characters')
				.oneOf(
					[ref('userPasswordConfirmation')],
					'Passwords must match',
				),
		userPasswordConfirmation: string().oneOf(
			[ref('userPassword')],
			'Passwords must match',
		),
	});
};

type CanUserEditGroupUser = ReturnType<typeof useUserMetaData> | UserOptionType;

export const canUserEditGroup = (group: UserGroupOption, user?: CanUserEditGroupUser) => {
	const userRoleInGroup = group.users?.find?.(
		groupUser => groupUser?.user?.userId === user?.userId,
	)?.userGroupUserRole;

	return userRoleInGroup === UserGroupRole.Admin || userRoleInGroup === UserGroupRole.Owner;
};
